import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { getFreshAccessToken } from "../../chat/api";
import ProjectDataTable from "../Projects/ProjectDataTable";
import useProjectRowActions from "../Projects/useProjectRowActions";
import { PROJECT_SOURCES, matchesSource } from "../Projects/projectAccess";

const MAX_ROWS = 30;
const COLUMNS_KEY = "projects_columnPreferences";

const TABS = [
  { key: PROJECT_SOURCES.ALL, label: "All" },
  { key: PROJECT_SOURCES.OWNED, label: "Mine" },
  { key: PROJECT_SOURCES.SHARED, label: "Shared with me" },
];

const EMPTY = {
  [PROJECT_SOURCES.ALL]: { title: "No projects yet", body: "Create your first project to get started. Your projects will show up here once added." },
  [PROJECT_SOURCES.OWNED]: { title: "No projects of your own yet", body: "Projects you create in this workspace will show up here." },
  [PROJECT_SOURCES.SHARED]: { title: "Nothing shared with you yet", body: "Projects teammates share with you will show up here." },
};

// Same saved column choices as the Projects page: local copy first, then the server's.
const useColumnPreferences = () => {
  const [columns, setColumns] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(COLUMNS_KEY) || "[]");
    } catch {
      return [];
    }
  });
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        await getFreshAccessToken();
        const res = await api.get("/api/v1/rfp/column-preferences/?view=rfp_folders");
        if (active && res.data?.columns) {
          setColumns(res.data.columns);
          localStorage.setItem(COLUMNS_KEY, JSON.stringify(res.data.columns));
        }
      } catch {
        // The table falls back to its default columns.
      }
    })();
    return () => {
      active = false;
    };
  }, []);
  return columns;
};

const noop = () => {};

const RecentProjects = ({ projects, loading }) => {
  const [tab, setTab] = useState(PROJECT_SOURCES.ALL);
  const columnPreferences = useColumnPreferences();
  const { tableProps, modals } = useProjectRowActions();

  const counts = useMemo(
    () => Object.fromEntries(TABS.map((t) => [t.key, projects.filter((p) => matchesSource(p, t.key)).length])),
    [projects]
  );
  const filtered = useMemo(() => projects.filter((p) => matchesSource(p, tab)), [projects, tab]);
  const rows = useMemo(() => filtered.slice(0, MAX_ROWS), [filtered]);
  const firstLoad = loading && !projects.length;

  return (
    <section className="bg-white border border-black/[0.08] rounded-xl shadow-[0_1px_3px_rgba(24,16,28,0.05)] px-5 py-3 flex flex-col gap-2.5 min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-4">
          <h2 className="m-0 text-[16px] font-medium text-[#18101C]">Recent projects</h2>
          <div className="flex gap-1 text-[13px]" role="tablist" aria-label="Filter projects">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                onClick={() => setTab(t.key)}
                className={`px-2.5 py-1 rounded-full border transition-colors ${
                  tab === t.key
                    ? "bg-[#2C1338] border-[#2C1338] text-white"
                    : "border-[#EDE6F0] text-[#73607B] hover:border-[#C95EBE]"
                }`}
              >
                {t.label}
                {!firstLoad && ` ${counts[t.key]}`}
              </button>
            ))}
          </div>
        </div>
        <Link
          to="/rfp-projects/my-projects"
          className="px-3.5 py-1.5 border border-[#C95EBE] !text-[#A04A97] rounded-lg text-[13px] font-medium hover:bg-[#C95EBE]/5"
        >
          View all
        </Link>
      </div>

      <ProjectDataTable
        {...tableProps}
        selectable={false}
        containerClassName="w-full max-h-[calc(100vh-660px)] overflow-auto custom-scrollbar relative border border-black/10 rounded-lg"
        columnPreferences={columnPreferences}
        filteredProjects={filtered}
        currentProjects={rows}
        loading={firstLoad}
        selectedProjects={[]}
        isAllChecked={false}
        handleSelectAll={noop}
        handleCheckboxChange={noop}
        selectableCount={0}
        emptyState={EMPTY[tab]}
      />

      {modals}
    </section>
  );
};

export default RecentProjects;
